// The verticals this workspace sells into — the single source of truth for
// every category-aware surface in the app: the sidebar, the /leads/<key>
// routes, the dashboard split, the command palette and the CSV/manifest
// loader.
//
// The list itself lives in categories.json (plain data, no imports) so the
// build scripts under scripts/ can read it under plain node, where they cannot
// parse JSX. This module adds the icon components on top.
//
// Adding a vertical is one entry in categories.json plus the matching slug in
// the terraform repo (`var.lead_categories` in modules/dynamodb/leads.tf, and
// `TABLES` in load_leads.py). Nothing else needs to change: the leads API takes
// its category → table map from terraform, and every component reads this list
// rather than hardcoding a slug.
//
// Fields per entry:
//   key       URL slug, DynamoDB table suffix, and public/data/<key>/ folder
//   label     short form — sidebar, chips, compact tables
//   plural    long form — page titles, dashboard cards, the palette
//   subtitle  directory page subtitle
//   keywords  extra command-palette search terms
//   icon      key into ICONS below
import rawCategories from './categories.json'
import { CloudIcon, HeartPulseIcon, BuildingIcon } from '../components/icons.jsx'

const ICONS = {
  cloud: CloudIcon,
  heartPulse: HeartPulseIcon,
  building: BuildingIcon,
}

export const CATEGORIES = rawCategories.map((c) => ({
  ...c,
  icon: ICONS[c.icon] || BuildingIcon,
}))

export const CATEGORY_KEYS = CATEGORIES.map((c) => c.key)

// Where the app lands when a route needs "the leads section" without caring
// which vertical — legacy redirects, empty-state CTAs, activity-log links.
export const DEFAULT_CATEGORY = CATEGORY_KEYS[0]

export const categoryPath = (key = DEFAULT_CATEGORY) => `/leads/${key}`
export const DEFAULT_CATEGORY_PATH = categoryPath(DEFAULT_CATEGORY)

export const getCategory = (key) => CATEGORIES.find((c) => c.key === key) || null

// Long form, falling back to the raw slug so an unknown category coming back
// from the API renders as itself instead of blank.
export const CATEGORY_LABEL = Object.fromEntries(
  CATEGORIES.map((c) => [c.key, c.plural]),
)

// Short form, same fallback rule.
export const CATEGORY_SHORT = Object.fromEntries(
  CATEGORIES.map((c) => [c.key, c.label]),
)

export const categoryLabel = (key) => CATEGORY_LABEL[key] || key
export const categoryShort = (key) => CATEGORY_SHORT[key] || key
